import React, { useState } from "react";
import { Check } from "lucide-react";
import { ChoiceList, TextArea, TextInput, POSITIONS, GOALS, BUDGETS, PREFIXES, AGES, START_WHEN } from "./Fields";
import { PrivacyPolicy } from "./PrivacyPolicy";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const digits = (s) => (s || "").replace(/\D/g, "");
export const isMinor = (a) => a.age != null && a.age < 18;
const isG = (a) => a.filled_by === "guardian";
const first = (a) => (a.player_name || "").trim().split(" ")[0] || "el futbolista";

const Q = ({ over, title, help }) => (
  <>
    <div className="qv-overline">{over}</div>
    <h1 className="emp-q" data-testid="empieza-question">{title}</h1>
    {help && <p className="emp-help">{help}</p>}
  </>
);

const AgeGrid = ({ a, set }) => (
  <div className="emp-ages" role="listbox" aria-label="Edad">
    {AGES.map((n) => (
      <button key={n} type="button" className={`emp-age${a.age === n ? " on" : ""}`} onClick={() => set({ age: n })} data-testid={`empieza-age-${n}`}>{n}</button>
    ))}
  </div>
);

const Contact = ({ a, set }) => {
  const minor = isMinor(a);
  return (
    <>
      <Q over="Contacto" title={minor ? "Datos del adulto responsable" : "¿Cómo te contacto?"}
        help={minor ? null : "Te escribiré por WhatsApp o email si tu caso encaja con el servicio."} />
      {minor && (
        <div className="emp-note" data-testid="empieza-minor-note">
          Como {first(a)} es menor de edad, el padre, madre o tutor legal es quien contrata el servicio y con quien hablaré. Deja aquí sus datos.
        </div>
      )}
      {minor && (
        <TextInput label="Nombre y apellidos del padre, madre o tutor" value={a.guardian_name} onChange={(v) => set({ guardian_name: v })}
          maxLength={120} autoComplete="name" testid="empieza-contact-guardian-name" />
      )}
      <div className="emp-field">
        <label>{minor ? "WhatsApp del padre, madre o tutor" : "Teléfono o WhatsApp"}</label>
        <div className="emp-phone">
          <select className="emp-select" value={a.prefix} onChange={(e) => set({ prefix: e.target.value })} data-testid="empieza-phone-prefix">
            {PREFIXES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <input className="emp-input" type="tel" inputMode="numeric" autoComplete="tel-national" placeholder="600 000 000" maxLength={18}
            value={a.phone} onChange={(e) => set({ phone: e.target.value.replace(/[^\d ]/g, "") })} data-testid="empieza-phone" />
        </div>
        <div style={{ fontSize: 12, color: "#a3a3a3" }} data-testid="empieza-phone-help">Usa el mismo número con el que me escribirás por WhatsApp.</div>
      </div>
      <TextInput label={minor ? "Email del padre, madre o tutor" : "Correo electrónico"} type="email" inputMode="email" autoComplete="email"
        placeholder="nombre@email.com" value={a.email} onChange={(v) => set({ email: v.trim() })} maxLength={120} testid="empieza-email" />
    </>
  );
};

const Consent = ({ a, set }) => {
  const [open, setOpen] = useState(false);
  const minor = isMinor(a);
  const text = minor
    ? `Soy ${a.guardian_name || "el padre, madre o tutor legal"}, padre, madre o tutor legal de ${a.player_name}, y doy mi consentimiento para el tratamiento de sus datos y de los míos conforme a la política de privacidad.`
    : isG(a)
      ? `Confirmo que ${a.player_name}, mayor de edad, conoce y acepta el envío de sus datos y la política de privacidad.`
      : "Soy mayor de edad y acepto el tratamiento de mis datos conforme a la política de privacidad.";
  return (
    <>
      <Q over="Privacidad" title="Último paso"
        help={minor ? "Al ser menor de 18 años, necesitamos el consentimiento del padre, madre o tutor legal." : "Solo necesito tu consentimiento para guardar tus respuestas y contactarte."} />
      <button type="button" className={`emp-check${a.consent ? " on" : ""}`} onClick={() => set({ consent: !a.consent })} data-testid="empieza-consent" aria-pressed={a.consent}
        style={{ width: "100%", textAlign: "left" }}>
        <span className="emp-tick">{a.consent && <Check size={14} strokeWidth={3} />}</span>
        <span>{text}</span>
      </button>
      <button type="button" className="emp-linkbtn" onClick={() => setOpen(!open)} data-testid="empieza-privacy-toggle">
        {open ? "Ocultar política de privacidad" : "Leer la política de privacidad"}
      </button>
      {open && <PrivacyPolicy minor={minor} />}
    </>
  );
};

export const STEPS = [
  {
    valid: (a) => !!a.filled_by, auto: true,
    render: (a, set) => (<>
      <Q over="Empecemos" title="¿Quién rellena el formulario?" />
      <ChoiceList prefix="empieza-filled" value={a.filled_by} onChange={(v) => set({ filled_by: v })}
        options={[["player", "Soy el futbolista"], ["guardian", "Soy el padre, madre o tutor"]]} />
    </>),
  },
  {
    valid: (a) => a.player_name.trim().length >= 2 && (!isG(a) || a.guardian_name.trim().length >= 2),
    render: (a, set) => (<>
      <Q over="Futbolista" title={isG(a) ? "¿Cómo se llama el futbolista?" : "¿Cómo te llamas?"} />
      <TextInput label="Nombre y apellidos del futbolista" value={a.player_name} onChange={(v) => set({ player_name: v })}
        maxLength={120} autoComplete={isG(a) ? "off" : "name"} testid="empieza-player-name" />
      {isG(a) && (
        <TextInput label="Tu nombre y apellidos (adulto responsable)" value={a.guardian_name} onChange={(v) => set({ guardian_name: v })}
          maxLength={120} autoComplete="name" testid="empieza-guardian-name" />
      )}
    </>),
  },
  {
    valid: (a) => a.age != null, auto: true,
    render: (a, set) => (<>
      <Q over="Edad" title={isG(a) ? `¿Qué edad tiene ${first(a)}?` : "¿Cuántos años tienes?"} />
      <AgeGrid a={a} set={set} />
    </>),
  },
  {
    valid: (a) => a.positions.length > 0,
    render: (a, set) => (<>
      <Q over="Posición" title={isG(a) ? "¿En qué posición juega?" : "¿En qué posición juegas?"} help="Puedes marcar varias." />
      <ChoiceList multi prefix="empieza-pos" options={POSITIONS} value={a.positions} onChange={(v) => set({ positions: v })} />
    </>),
  },
  {
    valid: (a) => a.goals.length > 0,
    render: (a, set) => (<>
      <Q over="Objetivo" title="¿Cuál es el principal objetivo esta temporada?" help="Puedes marcar varios." />
      <ChoiceList multi prefix="empieza-goal" options={GOALS} value={a.goals} onChange={(v) => set({ goals: v })} />
      <div style={{ marginTop: 18 }}>
        <TextArea value={a.goals_detail} onChange={(v) => set({ goals_detail: v })} max={200} rows={3}
          placeholder="Si quieres, cuéntame un poco más (opcional)" testid="empieza-goals-detail" />
      </div>
    </>),
  },
  {
    valid: () => true,
    render: (a, set) => (<>
      <Q over="Situación actual" title="¿Cómo es la situación ahora mismo?"
        help="¿Cómo te está yendo esta temporada? ¿Has trabajado antes con un preparador físico? ¿Qué has intentado mejorar hasta ahora?" />
      <TextArea value={a.current_situation} onChange={(v) => set({ current_situation: v })} max={400} rows={6}
        placeholder="Escribe aquí…" testid="empieza-situation" />
    </>),
  },
  {
    valid: (a) => a.obstacle.trim().length >= 2,
    render: (a, set) => (<>
      <Q over="Obstáculo" title="El principal obstáculo" help="¿Cuál es el principal obstáculo que te impide alcanzar tu objetivo?" />
      <TextArea value={a.obstacle} onChange={(v) => set({ obstacle: v })} max={250} rows={4} placeholder="Escribe aquí…" testid="empieza-obstacle" />
    </>),
  },
  {
    valid: (a) => !!a.budget, auto: true,
    render: (a, set) => (<>
      <Q over="Inversión" title="¿Cuál es el presupuesto mensual?" />
      <ChoiceList prefix="empieza-budget" options={BUDGETS} value={a.budget} onChange={(v) => set({ budget: v })} />
    </>),
  },
  {
    valid: (a) => !!a.start_when, auto: true,
    render: (a, set) => (<>
      <Q over="Inicio" title="¿Cuándo te gustaría empezar?" />
      <ChoiceList prefix="empieza-start" options={START_WHEN} value={a.start_when} onChange={(v) => set({ start_when: v })} />
    </>),
  },
  {
    valid: (a) => digits(a.phone).length >= 6 && digits(a.phone).length <= 15 && EMAIL_RE.test(a.email)
      && (!isMinor(a) || a.guardian_name.trim().length >= 2),
    render: (a, set) => <Contact a={a} set={set} />,
  },
  { valid: (a) => a.consent, render: (a, set) => <Consent a={a} set={set} /> },
];

export const buildPayload = (a) => ({
  filled_by: a.filled_by,
  player_name: a.player_name.trim(),
  guardian_name: (isG(a) || isMinor(a)) ? a.guardian_name.trim() : (a.guardian_name.trim() || null),
  age: a.age,
  positions: a.positions,
  goals: a.goals,
  goals_detail: a.goals_detail.trim() || null,
  current_situation: a.current_situation.trim() || null,
  obstacle: a.obstacle.trim(),
  budget: a.budget,
  start_when: a.start_when,
  phone: `${a.prefix} ${digits(a.phone)}`,
  email: a.email.trim().toLowerCase(),
  consent: a.consent,
  website: a.website || null,
});
